<template>
  <div 
    class="tile" 
    :style="{ backgroundColor }"
    @click.stop.prevent="handleClick"
    @touchstart.passive="handleTouchStart"
    @touchend.passive="handleTouchEnd"
  >
    <div class="tile-content">
      <img 
        :src="iconUrl" 
        alt="" 
        class="tile-icon"
        :class="{'default-icon': iconUrl === '/bookmark.svg'}"
      >
      <div 
        class="tile-name"
        :class="{'empty-tile-name': isEmpty}"
      >{{ name }}</div>
    </div>
    
    <!-- メニューボタン - PCでのみ表示 -->
    <div 
      class="tile-menu-button" 
      @click.stop="toggleMenu"
      v-if="!isMobile"
    >&#8942;</div>
    
    <!-- ドロップダウンメニュー -->
    <div 
      class="tile-dropdown-menu" 
      v-if="isMenuOpen && !isMobile"
      @click.stop
    >
      <div class="tile-menu-item" @click.stop="handleEdit">編集</div>
      <div class="tile-menu-item" @click.stop="handleDelete">削除</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BookmarkTile',
  props: {
    name: {
      type: String,
      default: '追加'
    },
    iconUrl: {
      type: String,
      default: '/bookmark.svg'
    },
    iconColor: {
      type: String,
      default: '#AAA'
    },
    backgroundColor: {
      type: String,
      default: '#f0f0f0'
    },
    isEmpty: {
      type: Boolean,
      default: true
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    url: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isMenuOpen: false,
      touchStartTime: 0,
      touchTimer: null,
      lastClickTime: 0,
      isLinkProcessing: false,   // リンク処理中フラグをdataに移動
      clickCounter: 0            // クリックカウンターをdataに移動
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    this.clearTouchTimer();
  },
  methods: {
    toggleMenu(event) {
      event.stopPropagation();
      this.isMenuOpen = !this.isMenuOpen;
    },
    
    // 編集メニュー項目のクリックハンドラ
    handleEdit() {
      this.$emit('edit');
      this.isMenuOpen = false;
    },
    
    // 削除メニュー項目のクリックハンドラ
    handleDelete() {
      this.$emit('delete');
      this.isMenuOpen = false;
    },
    
    handleOutsideClick(event) {
      if (!this.$el.contains(event.target) && this.isMenuOpen) {
        this.isMenuOpen = false;
      }
    },
    
    // 完全に新しいクリックハンドラ - 独自にリンク処理をする
    handleClick(event) {
      // クリックカウンタ（デバッグ用）
      this.clickCounter += 1;
      const clickId = this.clickCounter;
      
      console.log(`[TileFixed] タイルがクリックされました #${clickId} URL: ${this.url}`);
      
      // イベントの伝播を完全に停止
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      
      // モバイルの場合は何もしない（タッチイベントで処理）
      if (this.isMobile) {
        console.log(`[TileFixed] #${clickId} モバイルなのでスキップ`);
        return;
      }
      
      // 既にリンク処理中の場合はスキップ
      if (this.isLinkProcessing) {
        console.log(`[TileFixed] #${clickId} 既に処理中なのでスキップ`);
        return;
      }
      
      // 連続クリック防止
      const now = Date.now();
      if (now - this.lastClickTime < 1000) {
        console.log(`[TileFixed] #${clickId} 連続クリックを検出したためスキップ`);
        return;
      }
      this.lastClickTime = now;
      
      // 空のタイル - 編集モーダル表示
      if (this.isEmpty) {
        console.log(`[TileFixed] #${clickId} 空のタイル - 編集イベント発行`);
        this.$emit('click');
        return;
      }
      
      // 非空のタイルで、URLがある場合
      if (!this.isEmpty && this.url && this.url !== '#') {
        // リンク処理フラグをセット
        this.isLinkProcessing = true;
        console.log(`[TileFixed] #${clickId} リンク処理開始: ${this.url}`);
        
        try {
          // リンクを直接開く
          window.open(this.url, '_blank', 'noopener,noreferrer');
          console.log(`[TileFixed] #${clickId} リンクを開きました`);
          
          // イベント発火しない - App.vueの処理を起動させない
        } catch (e) {
          console.error(`[TileFixed] #${clickId} リンクを開く際にエラー: ${e}`);
          
          // エラー時のみApp.vueにフォールバック処理を委任
          this.$emit('click', {
            url: this.url,
            name: this.name,
            isEmpty: false,
            errorFallback: true,
            id: this.id
          });
        }
        
        // 1秒後にフラグをリセット
        setTimeout(() => {
          this.isLinkProcessing = false;
          console.log(`[TileFixed] #${clickId} リンク処理フラグリセット`);
        }, 1000);
      }
    },
    
    handleTouchStart() {
      if (!this.isMobile) return;
      
      this.touchStartTime = Date.now();
      
      this.clearTouchTimer();
      this.touchTimer = setTimeout(() => {
        this.$emit('edit');
        this.touchStartTime = 0;
      }, 700);
    },
    
    handleTouchEnd() {
      if (!this.isMobile) return;
      
      this.clearTouchTimer();
      
      const touchDuration = Date.now() - this.touchStartTime;
      if (touchDuration < 300 && this.touchStartTime > 0) {
        // モバイルでの短いタップ - クリックイベントを発火
        if (this.isEmpty) {
          this.$emit('click');
        } else if (this.url) {
          this.$emit('click', {
            url: this.url,
            name: this.name,
            isEmpty: this.isEmpty
          });
          
          // フォールバック - 直接リンクを開く
          try {
            window.open(this.url, '_blank');
          } catch (e) {
            console.error('リンクを開く際にエラーが発生しました', e);
          }
        }
      }
      
      this.touchStartTime = 0;
    },
    
    clearTouchTimer() {
      if (this.touchTimer) {
        clearTimeout(this.touchTimer);
        this.touchTimer = null;
      }
    }
  }
}
</script>

<style scoped>
/* タイルの基本スタイル */
.tile {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  background-color: #f0f0f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  box-sizing: border-box;
  cursor: pointer;
  overflow: hidden;
}

.tile-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.tile-icon {
  width: 32px;
  height: 32px;
  margin-bottom: 8px;
  object-fit: contain;
}

.tile-name {
  font-size: 12px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  white-space: nowrap;
  max-width: 80px;
}

.empty-tile-name {
  color: #AAA;
}

.tile-menu-button {
  position: absolute;
  top: 5px;
  right: 5px;
  font-size: 16px;
  color: #666;
  padding: 2px 5px;
  cursor: pointer;
  z-index: 10;
}

.tile-dropdown-menu {
  position: absolute;
  top: 25px;
  right: 5px;
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  border-radius: 4px;
  overflow: hidden;
  z-index: 20;
  min-width: 80px;
}

.tile-menu-item {
  padding: 8px 12px;
  font-size: 12px;
  color: #333;
  cursor: pointer;
}

.tile-menu-item:hover {
  background: #f5f5f5;
}

.default-icon {
  filter: invert(70%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%);
}
</style>
